import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceConseil = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Conseil, audit & DSI à temps partagé
          </h1>
          <p className="text-xl text-gray-600">
            J'interviens comme conseiller externe pour faire le point sur votre informatique : audit de l'existant, identification des risques, priorités et pistes d'amélioration.
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              Pour les structures qui en ont besoin, je peux jouer un rôle de <strong>"DSI à temps partagé"</strong> : 
              définition de votre feuille de route IT, arbitrage des projets, aide au choix des prestataires et suivi dans la durée, 
              sur un rythme adapté (quelques jours par an ou par trimestre).
            </p>
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes interventions</h2>
          <div className="space-y-3">
            {[
              'Audit technique complet de votre infrastructure',
              'Identification des risques et des points de vigilance',
              'Évaluation de la pertinence de vos outils actuels',
              'Définition d\'une feuille de route IT alignée avec vos objectifs',
              'Arbitrage et priorisation des projets informatiques',
              'Aide au choix de prestataires et de solutions',
              'Suivi de projets et validation des livrables',
              'Veille technologique et recommandations d\'évolution',
              'Accompagnement lors de moments clés (croissance, fusion, etc.)'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Les avantages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vision stratégique</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Une feuille de route claire pour faire évoluer votre IT</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Expertise externe</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Un regard neuf et objectif sur votre infrastructure</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Maîtrise budgétaire</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Priorisation des investissements selon vos enjeux</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Flexibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Un accompagnement au rythme qui vous convient</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Pour qui ?</h3>
            <p className="text-gray-600 mb-4">
              Ce service s'adresse aux entreprises qui n'ont pas de responsable IT en interne, ou qui souhaitent un regard extérieur 
              pour valider leurs choix et structurer leur démarche.
            </p>
            <p className="text-gray-600">
              L'objectif : vous donner la visibilité et les clés pour piloter sereinement votre informatique, sans nécessairement recruter à temps plein.
            </p>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Besoin de conseil ou d'un audit ?</h3>
          <p className="text-gray-600 mb-6">
            Discutons de votre situation et voyons comment je peux vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};